import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../api/client.js';
import { useAuth } from '../auth/AuthContext.jsx';
import {
  Badge, Breadcrumbs, Card, Empty, ErrorNotice, Loading, Modal, Notice, Pager,
  fmt, useToast, vehicleLabel,
} from '../components/ui.jsx';

const LIMIT = 25;

const EMPTY_FORM = {
  make: '', model: '', variant: '', vehicleClassId: '', fuelType: 'petrol',
  cubicCapacity: '', kilowatt: '', seatingCapacity: '2', bodyType: '', segment: '',
  launchYear: '', status: 'active',
};

export default function VehiclesPage() {
  const { can } = useAuth();
  const [filters, setFilters] = useState({ search: '', status: '', fuelType: '', sort: 'make' });
  const [offset, setOffset] = useState(0);
  const [addOpen, setAddOpen] = useState(false);

  const query = useQuery({
    queryKey: ['vehicles', filters, offset],
    queryFn: () => api.listVehicles({ ...filters, limit: LIMIT, offset }),
  });

  const rows = query.data?.data || [];
  const total = query.data?.meta?.total || 0;

  const setFilter = (k, v) => { setFilters((f) => ({ ...f, [k]: v })); setOffset(0); };

  return (
    <div className="page">
      <Breadcrumbs items={[{ label: 'Dashboard', to: '/dashboard' }, { label: 'GIIB master' }]} />
      <div className="page-head">
        <div>
          <h1>GIIB vehicle master</h1>
          <div className="page-sub">
            The single source of truth. Adding a vehicle here mints its universal key; insurer codes attach
            to it afterwards, when each insurer's master is imported.
          </div>
        </div>
        <div className="spacer" />
        {can('reviewer') && (
          <button className="btn btn-primary" onClick={() => setAddOpen(true)}>Add vehicle</button>
        )}
      </div>

      <Notice tone="info" title="New vehicle launched in the market? Do this in order">
        1. Add the vehicle here so it receives a universal key.
        2. Import the insurer master that now carries it (<Link to="/upload">Upload master</Link>).
        3. The engine matches the insurer's new code to this vehicle; approve it in the
        {' '}<Link to="/mappings">review queue</Link>. Repeat step 2 for each insurer as their file arrives.
      </Notice>

      <Card bodyClass="tight">
        <div className="filters">
          <div className="f grow">
            <label htmlFor="v-search">Search</label>
            <input
              id="v-search" placeholder="Make, model, variant or universal key"
              value={filters.search} onChange={(e) => setFilter('search', e.target.value)}
            />
          </div>
          <div className="f">
            <label htmlFor="v-status">Status</label>
            <select id="v-status" value={filters.status} onChange={(e) => setFilter('status', e.target.value)}>
              <option value="">All</option>
              <option value="active">Active</option>
              <option value="pending">Pending</option>
              <option value="retired">Retired</option>
            </select>
          </div>
          <div className="f">
            <label htmlFor="v-fuel">Fuel</label>
            <select id="v-fuel" value={filters.fuelType} onChange={(e) => setFilter('fuelType', e.target.value)}>
              <option value="">All</option>
              <option value="petrol">Petrol</option>
              <option value="diesel">Diesel</option>
              <option value="electric">Electric</option>
              <option value="cng">CNG</option>
              <option value="hybrid">Hybrid</option>
            </select>
          </div>
          <div className="f">
            <label htmlFor="v-sort">Sort</label>
            <select id="v-sort" value={filters.sort} onChange={(e) => setFilter('sort', e.target.value)}>
              <option value="make">Make and model</option>
              <option value="newest">Recently added</option>
              <option value="cc">Capacity</option>
              <option value="key">Universal key</option>
            </select>
          </div>
        </div>

        {query.isLoading ? <div style={{ padding: 16 }}><Loading rows={6} /></div>
          : query.error ? <div style={{ padding: 16 }}><ErrorNotice error={query.error} /></div>
            : rows.length === 0 ? <Empty title="No vehicles match these filters" />
              : (
                <>
                  <div className="table-wrap">
                    <table>
                      <thead>
                        <tr>
                          <th>Universal key</th>
                          <th>Vehicle</th>
                          <th className="num">cc</th>
                          <th className="num">kW</th>
                          <th>Fuel</th>
                          <th className="num">Seats</th>
                          <th>Status</th>
                          <th className="num">Linked ICs</th>
                        </tr>
                      </thead>
                      <tbody>
                        {rows.map((v) => (
                          <tr key={v.id}>
                            <td><Link className="mono" to={`/registry/${v.universalKey}`}>{v.universalKey}</Link></td>
                            <td>
                              <div className="cell-main">{vehicleLabel(v)}</div>
                              {v.sourceRef && <div className="prov">source row {v.sourceRef}</div>}
                            </td>
                            <td className="num">{v.cubicCapacity ?? '-'}</td>
                            <td className="num">{v.kilowatt ?? '-'}</td>
                            <td>{v.fuelType || '-'}</td>
                            <td className="num">{v.seatingCapacity ?? '-'}</td>
                            <td><Badge kind={v.status === 'active' ? 'approved' : v.status === 'retired' ? 'superseded' : 'pending'}>{v.status}</Badge></td>
                            <td className="num">{fmt(v.linkedInsurers ?? 0)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  <Pager total={total} limit={LIMIT} offset={offset} onChange={setOffset} />
                </>
              )}
      </Card>

      {addOpen && <AddVehicleModal onClose={() => setAddOpen(false)} />}
    </div>
  );
}

function AddVehicleModal({ onClose }) {
  const toast = useToast();
  const queryClient = useQueryClient();
  const [form, setForm] = useState(EMPTY_FORM);
  const [error, setError] = useState(null);
  const [duplicate, setDuplicate] = useState(null);
  const [force, setForce] = useState(false);

  const classes = useQuery({ queryKey: ['vehicleClasses'], queryFn: () => api.vehicleClasses().then((r) => r.data) });

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const payload = () => ({
    make: form.make.trim(),
    model: form.model.trim(),
    variant: form.variant.trim() || null,
    vehicleClassId: form.vehicleClassId ? Number(form.vehicleClassId) : null,
    fuelType: form.fuelType || null,
    cubicCapacity: form.cubicCapacity ? Number(form.cubicCapacity) : null,
    kilowatt: form.kilowatt ? Number(form.kilowatt) : null,
    seatingCapacity: form.seatingCapacity ? Number(form.seatingCapacity) : null,
    bodyType: form.bodyType.trim() || null,
    segment: form.segment.trim() || null,
    launchYear: form.launchYear ? Number(form.launchYear) : null,
    status: form.status,
    forceCreate: force,
  });

  // Duplicate check runs on blur of the variant field, before submission, so
  // the analyst is warned rather than blocked at the end.
  const checkDuplicate = async () => {
    if (!form.make.trim() || !form.model.trim()) return;
    try {
      const res = await api.checkDuplicate(payload());
      setDuplicate(res.data);
    } catch { /* the create call will surface any real problem */ }
  };

  const mutation = useMutation({
    mutationFn: () => api.createVehicle(payload()).then((r) => r.data),
    onSuccess: (vehicle) => {
      toast.success(`${vehicleLabel(vehicle)} added as ${vehicle.universalKey}`);
      queryClient.invalidateQueries({ queryKey: ['vehicles'] });
      queryClient.invalidateQueries({ queryKey: ['overview'] });
      onClose();
    },
    onError: setError,
  });

  const valid = form.make.trim() && form.model.trim() && (form.cubicCapacity || form.kilowatt);

  return (
    <Modal
      title="Add a vehicle to the GIIB master"
      onClose={onClose}
      wide
      footer={(
        <>
          <button className="btn btn-primary" disabled={!valid || mutation.isPending} onClick={() => mutation.mutate()}>
            {mutation.isPending ? 'Saving...' : 'Add vehicle and mint universal key'}
          </button>
          <button className="btn" onClick={onClose}>Cancel</button>
        </>
      )}
    >
      <ErrorNotice error={error} />

      {duplicate && (
        <Notice tone="warn" title="A very similar vehicle already exists">
          {vehicleLabel(duplicate)} ({duplicate.universalKey}, {duplicate.cubicCapacity ?? '-'} cc).
          Map the insurer code to that key instead of creating a second identity for the same vehicle.
          <label style={{ display: 'flex', gap: 6, alignItems: 'center', marginTop: 8 }}>
            <input type="checkbox" checked={force} onChange={(e) => setForce(e.target.checked)} />
            This is genuinely a different variant - create it anyway
          </label>
        </Notice>
      )}

      <div className="grid-2">
        <div className="field">
          <label htmlFor="a-make">Make *</label>
          <input id="a-make" value={form.make} onChange={set('make')} onBlur={checkDuplicate} placeholder="Hero" />
        </div>
        <div className="field">
          <label htmlFor="a-model">Model *</label>
          <input id="a-model" value={form.model} onChange={set('model')} onBlur={checkDuplicate} placeholder="Splendor Plus" />
        </div>
        <div className="field">
          <label htmlFor="a-variant">Variant</label>
          <input id="a-variant" value={form.variant} onChange={set('variant')} onBlur={checkDuplicate} placeholder="Self Start Alloy Wheel" />
          <div className="field-hint">Write it the way the RTO or the OEM brochure does, not in abbreviations.</div>
        </div>
        <div className="field">
          <label htmlFor="a-class">Vehicle class</label>
          <select id="a-class" value={form.vehicleClassId} onChange={set('vehicleClassId')}>
            <option value="">Not set</option>
            {(classes.data || []).map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
        </div>
      </div>

      <div className="grid-3">
        <div className="field">
          <label htmlFor="a-fuel">Fuel type</label>
          <select id="a-fuel" value={form.fuelType} onChange={set('fuelType')}>
            <option value="petrol">Petrol</option>
            <option value="diesel">Diesel</option>
            <option value="electric">Electric</option>
            <option value="cng">CNG</option>
            <option value="lpg">LPG</option>
            <option value="hybrid">Hybrid</option>
          </select>
        </div>
        <div className="field">
          <label htmlFor="a-cc">Cubic capacity</label>
          <input id="a-cc" type="number" step="0.01" value={form.cubicCapacity} onChange={set('cubicCapacity')} onBlur={checkDuplicate} />
          <div className="field-hint">Required unless the vehicle is electric.</div>
        </div>
        <div className="field">
          <label htmlFor="a-kw">Power (kW)</label>
          <input id="a-kw" type="number" step="0.001" value={form.kilowatt} onChange={set('kilowatt')} />
          <div className="field-hint">Electric vehicles are rated on kW, so this drives their band.</div>
        </div>
        <div className="field">
          <label htmlFor="a-seats">Seating capacity</label>
          <input id="a-seats" type="number" min="1" value={form.seatingCapacity} onChange={set('seatingCapacity')} />
        </div>
        <div className="field">
          <label htmlFor="a-body">Body type</label>
          <input id="a-body" value={form.bodyType} onChange={set('bodyType')} placeholder="Motorcycle" />
        </div>
        <div className="field">
          <label htmlFor="a-year">Launch year</label>
          <input id="a-year" type="number" min="1950" max="2100" value={form.launchYear} onChange={set('launchYear')} />
        </div>
      </div>

      <Notice tone="info" title="What happens next">
        The vehicle is saved with a new universal key and no insurer codes. When you import the master of an
        insurer that now carries this vehicle, the engine will score its new code against this record and
        propose the link for approval.
      </Notice>
    </Modal>
  );
}
